// ============================================================
// hooks/useEscalations.js — All API calls + global state
// ============================================================

import { useEffect, useState, useContext } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { LanguageContext } from "../../../constant/LanguageContext";
import translations from "../../../utils/translations";

const API_URL = process.env.REACT_APP_BASE_URL;

/**
 * Central data hook for EscalationComplaints.
 * Exposes:
 *   - state: escalations, assignedEscalations, groups, stats, loading, error, success
 *   - actions: fetchMyEscalations, fetchGroups, fetchEscalations,
 *              handleSubmitEscalation, handleUpdateStatus,
 *              selectedGroup / setSelectedGroup,
 *              setError, setSuccess
 */
const useEscalations = () => {
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.en;
  
  const [escalations, setEscalations]               = useState([]);
  const [assignedEscalations, setAssignedEscalations] = useState([]);
  const [groups, setGroups]                         = useState([]);
  const [selectedGroup, setSelectedGroup]           = useState("all");
  // Starts true (not false) so the page-level skeleton is guaranteed to be
  // visible on first paint instead of racing the mount effect that flips it.
  const [loading, setLoading]                       = useState(true);
  const [error, setError]                           = useState("");
  const [success, setSuccess]                       = useState("");

  const [stats, setStats] = useState({
    total:      0,
    pending:    0,
    inProgress: 0,
    resolved:   0,
    rejected:   0,
  });

  // ── My Escalations ────────────────────────────────────────
  const fetchMyEscalations = async () => {
    try {
      setLoading(true);
      const res = await apiFetch(`${API_URL}/api/escalations/my-escalations`, {
        method: "GET",
      });
      if (!res.ok) throw new Error("Failed to fetch escalations");

      const data = await res.json();
      const mapped = data.data.escalations.map((e) => ({
        id:          e.id,
        type:        e.type,
        status:      e.status,
        issuedAt:    e.issuedAt,
        description: e.description,
      }));

      setEscalations(mapped);
      setStats({
        total:      mapped.length,
        inProgress: mapped.filter((e) => e.status === "IN_PROGRESS").length,
        pending:    mapped.filter((e) => e.status === "PENDING").length,
        resolved:   mapped.filter((e) => e.status === "RESOLVED").length,
        rejected:   mapped.filter((e) => e.status === "REJECTED").length,
      });
    } catch (err) {
      console.error("fetchMyEscalations:", err);
      setError("Failed to load escalations");
    } finally {
      setLoading(false);
    }
  };

  // ── Groups ────────────────────────────────────────────────
  const fetchGroups = async () => {
    try {
      const res = await apiFetch(`${API_URL}/api/groups/member-of-groups`, {
        method: "GET",
      });
      const data = await res.json();
      setGroups(data || []);
    } catch (err) {
      console.error("fetchGroups:", err);
    }
  };

  // ── Assigned Escalations ─────────────────────────────────
  const fetchEscalations = async () => {
    setLoading(true);
    try {
      if (selectedGroup === "all") {
        const all = await Promise.all(
          groups.map(async (group) => {
            const res = await apiFetch(
              `${API_URL}/api/escalations/${group.groupId}/escalations`,
              { method: "GET" }
            );
            const data = await res.json();
            return (
              data.data?.map((item) => ({
                ...item.escalation,
                groupName: group.groupName,
                groupId:   group.groupId,
              })) || []
            );
          })
        );
        setAssignedEscalations(all.flat());
      } else {
        const res = await apiFetch(
          `${API_URL}/api/escalations/${selectedGroup}/escalations`,
          { method: "GET" }
        );
        const data     = await res.json();
        const groupName = groups.find((g) => g.groupId === selectedGroup)?.groupName;
        setAssignedEscalations(
          data.data?.map((item) => ({
            ...item.escalation,
            groupName,
            groupId: selectedGroup,
          })) || []
        );
      }
    } catch (err) {
      console.error("fetchEscalations:", err);
      setAssignedEscalations([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Create ────────────────────────────────────────────────
  const handleSubmitEscalation = async (formData) => {
    try {
      setLoading(true);
      const res = await apiFetch(`${API_URL}/api/escalations`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(formData),
      });
      if (!res.ok) throw new Error("Failed to create escalation");

      const data = await res.json();
      if (data.forwarded === false) {
        setSuccess(t.escalationCreatedNoGroup);
      } else {
        setSuccess(t.escalationCreated);
      }
      fetchMyEscalations();
    } catch (err) {
      setError(t.escalationCreationFailed);
    } finally {
      setLoading(false);
    }
  };

  // ── Update Status ─────────────────────────────────────────
  const handleUpdateStatus = async (escalationId, newStatus) => {
    try {
      setLoading(true);
      const res = await apiFetch(`${API_URL}/api/escalations/${escalationId}`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error("Failed to update status");
      fetchEscalations();
    } catch (err) {
      console.error("handleUpdateStatus:", err);
    } finally {
      setLoading(false);
    }
  };

  return {
    // state
    escalations,
    assignedEscalations,
    groups,
    selectedGroup,
    loading,
    error,
    success,
    stats,

    // actions
    setSelectedGroup,
    setError,
    setSuccess,
    fetchMyEscalations,
    fetchGroups,
    fetchEscalations,
    handleSubmitEscalation,
    handleUpdateStatus,
  };
};

export default useEscalations;